import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';

const WishlistContext = createContext(null);
const STORAGE_KEY = 'webfolio_wishlist_v1';

function safeParseItems(value) {
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x) => x && typeof x === 'object')
      .map((x) => ({
        id: String(x.id ?? ''),
        slug: String(x.slug ?? ''),
        name: String(x.name ?? ''),
        price: Number(x.price ?? 0),
        priceText: x.priceText != null ? String(x.priceText) : null,
        image: x.image ? String(x.image) : undefined,
        outOfStock: !!x.outOfStock,
      }))
      .filter((x) => x.id && x.slug && x.name);
  } catch (_) {
    return [];
  }
}

function reducer(state, action) {
  switch (action.type) {
    case 'toggle': {
      const item = action.item;
      const exists = state.items.some((x) => x.id === item.id);
      const items = exists
        ? state.items.filter((x) => x.id !== item.id)
        : [...state.items, item];
      return { ...state, items };
    }
    case 'add': {
      if (state.items.some((x) => x.id === action.item.id)) return state;
      return { ...state, items: [...state.items, action.item] };
    }
    case 'remove': {
      return { ...state, items: state.items.filter((x) => x.id !== action.id) };
    }
    case 'clear':
      return { ...state, items: [] };
    default:
      return state;
  }
}

export function WishlistProvider({ children }) {
  const [state, dispatch] = useReducer(
    reducer,
    { items: [] },
    () => {
      if (typeof window === 'undefined') return { items: [] };
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return { items: [] };
      return { items: safeParseItems(raw) };
    },
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
    } catch (_) {}
  }, [state.items]);

  const value = useMemo(() => {
    const count = state.items.length;
    return {
      items: state.items,
      wishlistCount: count,
      isWishlisted: (id) => state.items.some((x) => x.id === id),
      toggleWishlist: (item) => dispatch({ type: 'toggle', item }),
      addWishlist: (item) => dispatch({ type: 'add', item }),
      removeWishlist: (id) => dispatch({ type: 'remove', id }),
      clearWishlist: () => dispatch({ type: 'clear' }),
    };
  }, [state.items]);

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider');
  return ctx;
}
